import { Suspense } from 'react';
import { CampaignsIcon } from './assets/campaigns-icon';
import { DonationsIcon } from './assets/donations-icon';
import { CollectedIcon } from './assets/collected-icon';
import { EmptyChar } from './empty-char';
import { useTranslation } from '@/lib/i18n';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { usePortalCampaignsQuery, usePortalMetadataQuery, usePortalCampaignsAggregatesQuery } from '@/lib/queries';
import { useClient } from '@worldcoo/ui/hooks/use-client';
import { useEphemeralStore } from '@/lib/app-store';
import { PortalAnimateNumber } from './portal-animate-number';
import { usePortalContext } from '@/lib/use-portal-context';
import { isNotFound } from '@tanstack/react-router';

export function Statistics() {
	return (
		<Suspense fallback={<StatisticsLoading />}>
			<SuspensedStatistics />
		</Suspense>
	);
}

function StatisticsLoading() {
	const { i18n } = useTranslation();

	return (
		<DivWithPrimaryColorVariable className="bg-background shadow-4xl flex w-full justify-between gap-5 rounded-2xl p-4 sm:px-6 sm:py-5 lg:shadow-none">
			<div className="flex w-full flex-col font-medium">
				<CampaignsIcon className="text-primary mb-2" />
				<div className="text-lg whitespace-nowrap sm:text-2xl">
					<EmptyChar />
				</div>
				<span className="text-primary text-xs sm:text-base">{i18n.statistics.campaigns}</span>
			</div>

			<div className="flex w-full flex-col font-medium">
				<DonationsIcon className="text-primary mb-2" />
				<div className="text-lg whitespace-nowrap sm:text-2xl">
					<EmptyChar />
				</div>
				<span className="text-primary text-xs sm:text-base">{i18n.statistics.donations}</span>
			</div>

			<div className="flex w-full flex-col font-medium">
				<CollectedIcon className="text-primary mb-2" />
				<div className="flex gap-1.25 text-lg whitespace-nowrap sm:text-2xl">
					<EmptyChar />
					{/* <span>0</span>
					<span>€</span> */}
				</div>
				<span className="text-primary text-xs sm:text-base">{i18n.statistics.collected}</span>
			</div>
		</DivWithPrimaryColorVariable>
	);
}

function SuspensedStatistics() {
	const { i18n } = useTranslation();
	const metadata = usePortalMetadataQuery();
	const aggregates = usePortalCampaignsAggregatesQuery();
	const pagesLoaded = useEphemeralStore((store) => store.pagesLoaded);
	const isHomeLoaded = pagesLoaded.includes('home');

	// Hide component when metadata or aggregates are unavailable
	if (isNotFound(metadata)) return null;
	if (!aggregates || !('totalAggregate' in aggregates)) {
		return null;
	}

	return (
		<DivWithPrimaryColorVariable className="bg-background shadow-4xl flex w-full justify-between gap-5 rounded-2xl p-4 sm:px-6 sm:py-5 lg:shadow-none">
			<div className="flex w-full flex-col font-medium">
				<CampaignsIcon className="text-primary mb-2" />
				<div className="text-lg whitespace-nowrap sm:text-2xl">
					<Campaigns textEffectDisabled={isHomeLoaded} />
				</div>
				<span className="text-primary text-xs sm:text-base">{i18n.statistics.campaigns}</span>
			</div>

			<div className="flex w-full flex-col font-medium">
				<DonationsIcon className="text-primary mb-2" />
				<div className="text-lg whitespace-nowrap sm:text-2xl">
					<Donations textEffectDisabled={isHomeLoaded} />
				</div>
				<span className="text-primary text-xs sm:text-base">{i18n.statistics.donations}</span>
			</div>

			{!metadata.hideCollected && (
				<div className="flex w-full flex-col font-medium">
					<CollectedIcon className="text-primary mb-2" />
					<div className="text-lg whitespace-nowrap sm:text-2xl">
						<Collected textEffectDisabled={isHomeLoaded} />
					</div>
					<span className="text-primary text-xs sm:text-base">{i18n.statistics.collected}</span>
				</div>
			)}
		</DivWithPrimaryColorVariable>
	);
}

function Campaigns(props: { textEffectDisabled?: boolean }) {
	const { locale } = usePortalContext();
	const data = usePortalCampaignsQuery();
	const isClient = useClient();

	if (isNotFound(data)) return null;

	const { campaignsCount } = data;
	const number = !props.textEffectDisabled && !isClient ? 0 : campaignsCount;

	return <PortalAnimateNumber locale={locale} value={number} />;
}

function Donations(props: { textEffectDisabled?: boolean }) {
	const { locale } = usePortalContext();
	const aggregates = usePortalCampaignsAggregatesQuery();
	const isClient = useClient();
	const number =
		!props.textEffectDisabled && !isClient
			? 0
			: aggregates && 'totalAggregate' in aggregates
				? aggregates.totalAggregate.donors
				: 0;

	return <PortalAnimateNumber testId="donations" locale={locale} value={number} />;
}

function Collected(props: { textEffectDisabled?: boolean }) {
	const { locale } = usePortalContext();
	const aggregates = usePortalCampaignsAggregatesQuery();
	const isClient = useClient();
	const number =
		!props.textEffectDisabled && !isClient
			? 0
			: aggregates && 'totalAggregate' in aggregates
				? aggregates.totalAggregate.collected
				: 0;

	return <PortalAnimateNumber testId="collected" locale={locale} value={number} suffix="€" />;
}
